import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Phone-related utilities
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds === 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM d, h:mm a");
  } catch {
    return dateString;
  }
}

export function formatPhoneEndReason(
  endReason: string | null | undefined
): string {
  if (!endReason) return "Unknown";
  return endReason
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

export function getPhoneEndReasonColor(
  endReason: string | null | undefined
): "default" | "secondary" | "destructive" | "outline" {
  if (!endReason) return "outline";
  const reason = endReason.toLowerCase();
  if (reason.includes("hangup") || reason.includes("hang up")) return "default";
  if (reason.includes("connected") || reason.includes("success"))
    return "default";
  if (reason.includes("error") || reason.includes("fail")) return "destructive";
  if (reason.includes("wrong") || reason.includes("invalid"))
    return "destructive";
  if (
    reason.includes("timeout") ||
    reason.includes("no answer") ||
    reason.includes("no-answer")
  )
    return "secondary";
  if (reason.includes("voicemail")) return "secondary";
  return "outline";
}

export function formatCustomerSentiment(
  sentiment: string | null | undefined
): string {
  if (!sentiment) return "-";
  const labels: Record<string, string> = {
    very_negative: "Very Negative",
    negative: "Negative",
    neutral: "Neutral",
    positive: "Positive",
    very_positive: "Very Positive",
  };
  return labels[sentiment] ?? sentiment;
}

export function getCustomerSentimentColor(
  sentiment: string | null | undefined
): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" {
  switch (sentiment) {
    case "very_negative":
      return "destructive";
    case "negative":
      return "warning";
    case "neutral":
      return "secondary";
    case "positive":
    case "very_positive":
      return "success";
    default:
      return "outline";
  }
}

export function getPhoneStatusColor(
  status: string | null | undefined
): "default" | "secondary" | "destructive" | "outline" {
  if (!status) return "outline";
  switch (status.toLowerCase()) {
    case "ended":
    case "completed":
      return "default";
    case "in-progress":
    case "queued":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}
